import type { MaterialId } from '../model/materials'
import { type Contour, type Pt, segmentSpans, signedDistance } from './polygon'

export type V3 = [number, number, number]
export type Caps = 'none' | 'top' | 'bottom' | 'both'

export const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
export const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
export const scale = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s]
export const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
export const len = (a: V3) => Math.hypot(a[0], a[1], a[2])
export const dist = (a: V3, b: V3) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
export const normalize = (a: V3): V3 => {
  const l = len(a) || 1
  return [a[0] / l, a[1] / l, a[2] / l]
}

export interface ShapeInput {
  id: string
  name?: string
  kind: 'box' | 'cylinder' | 'tube' | 'prism'
  position: V3
  rotation: V3 // Euler YXZ radians (matches three.js with rotation.order = 'YXZ')
  size: V3
  material: MaterialId
  /** Tube wall thickness (m); also the thickness of any end plate. */
  wall?: number
  caps?: Caps
  /** Prism footprint in unit coordinates (scaled by size x and z), and any holes. */
  outline?: Pt[]
  holes?: Pt[][]
}

/** Row-major 3x3 rotation matrix: world = R * local. */
export type Mat3 = [number, number, number, number, number, number, number, number, number]

/** three.js 'YXZ' Euler → rotation matrix (R = Ry · Rx · Rz); the same order is used for primitives and the head. */
export function eulerToMat3([x, y, z]: V3): Mat3 {
  const a = Math.cos(x),
    b = Math.sin(x)
  const c = Math.cos(y),
    d = Math.sin(y)
  const e = Math.cos(z),
    f = Math.sin(z)
  const ce = c * e,
    cf = c * f,
    de = d * e,
    df = d * f
  return [ce + df * b, de * b - cf, a * d, a * f, a * e, -b, cf * b - de, df + ce * b, a * c]
}

/** The empty part of a tube: an inner elliptical cylinder, closed or open at each end. */
export interface Bore {
  rx: number
  rz: number
  y0: number
  y1: number
}

export interface Frame {
  shape: ShapeInput
  r: Mat3
  half: V3
  /** Tubes only. */
  bore?: Bore
  /** Prisms only: outline and holes in local metres (the unit outline scaled by size). */
  contours?: Contour[]
  /** World-space half extents of the shape's bounding box (centre = shape position): lets segment tests skip shapes they cannot touch. */
  reach: V3
}

export function makeFrame(shape: ShapeInput): Frame {
  const half: V3 = [Math.abs(shape.size[0]) / 2, Math.abs(shape.size[1]) / 2, Math.abs(shape.size[2]) / 2]
  const r = eulerToMat3(shape.rotation)
  const frame: Frame = { shape, r, half, reach: [0, 0, 0] }
  if (shape.kind === 'tube') frame.bore = tubeBore(shape, half)
  // Prism outlines may extend beyond the unit square, so bound them by their own extent.
  let ext: V3 = half
  if (shape.kind === 'prism') {
    frame.contours = prismContours(shape)
    const outer = frame.contours[0] ?? []
    ext = [Math.max(0, ...outer.map((p) => Math.abs(p[0]))), half[1], Math.max(0, ...outer.map((p) => Math.abs(p[1])))]
  }
  frame.reach = [
    Math.abs(r[0]) * ext[0] + Math.abs(r[1]) * ext[1] + Math.abs(r[2]) * ext[2],
    Math.abs(r[3]) * ext[0] + Math.abs(r[4]) * ext[1] + Math.abs(r[5]) * ext[2],
    Math.abs(r[6]) * ext[0] + Math.abs(r[7]) * ext[1] + Math.abs(r[8]) * ext[2],
  ]
  return frame
}

/** A prism's outline and holes scaled to local metres. */
export function prismContours(shape: Pick<ShapeInput, 'size' | 'outline' | 'holes'>): Contour[] {
  const sx = Math.abs(shape.size[0])
  const sz = Math.abs(shape.size[2])
  const scaled = (c: Pt[]): Contour => c.map(([x, z]) => [x * sx, z * sz] as Pt)
  return [scaled(shape.outline ?? []), ...(shape.holes ?? []).map(scaled)]
}

/** Inner void of a tube. An open end extends the bore past the outer surface so nothing closes it. */
export function tubeBore(shape: Pick<ShapeInput, 'wall' | 'caps'>, half: V3): Bore {
  const wall = Math.min(Math.max(shape.wall ?? 1, 0.01), 0.98 * Math.min(half[0], half[2]))
  const caps = shape.caps ?? 'none'
  const open = Math.max(half[1], 1) + 1
  return {
    rx: Math.max(half[0] - wall, 0.005),
    rz: Math.max(half[2] - wall, 0.005),
    y0: caps === 'bottom' || caps === 'both' ? -half[1] + wall : -half[1] - open,
    y1: caps === 'top' || caps === 'both' ? half[1] - wall : half[1] + open,
  }
}

/** World point → shape-local (rotation only, no scale). */
export function toLocal(f: Frame, p: V3): V3 {
  const d = sub(p, f.shape.position)
  const r = f.r
  return [r[0] * d[0] + r[3] * d[1] + r[6] * d[2], r[1] * d[0] + r[4] * d[1] + r[7] * d[2], r[2] * d[0] + r[5] * d[1] + r[8] * d[2]]
}

export function toWorldDir(f: Frame, v: V3): V3 {
  const r = f.r
  return [r[0] * v[0] + r[1] * v[1] + r[2] * v[2], r[3] * v[0] + r[4] * v[1] + r[5] * v[2], r[6] * v[0] + r[7] * v[1] + r[8] * v[2]]
}

export function toWorld(f: Frame, l: V3): V3 {
  return add(toWorldDir(f, l), f.shape.position)
}

type Span = [number, number]

function clipBox(la: V3, d: V3, half: V3): Span | null {
  let t0 = 0
  let t1 = 1
  for (const axis of [0, 1, 2] as const) {
    const h = half[axis]
    const o = la[axis]
    const dd = d[axis]
    if (Math.abs(dd) < 1e-12) {
      if (o < -h || o > h) return null
      continue
    }
    let ta = (-h - o) / dd
    let tb = (h - o) / dd
    if (ta > tb) [ta, tb] = [tb, ta]
    t0 = Math.max(t0, ta)
    t1 = Math.min(t1, tb)
    if (t0 > t1) return null
  }
  return [t0, t1]
}

/** Parametric span of segment (la + t·d, t ∈ [0,1]) inside an elliptical cylinder (axis = local Y) over y ∈ [y0, y1]. */
function cylSpan(la: V3, d: V3, rx: number, rz: number, y0: number, y1: number): Span | null {
  let t0 = 0
  let t1 = 1
  if (Math.abs(d[1]) < 1e-12) {
    if (la[1] < y0 || la[1] > y1) return null
  } else {
    let ta = (y0 - la[1]) / d[1]
    let tb = (y1 - la[1]) / d[1]
    if (ta > tb) [ta, tb] = [tb, ta]
    t0 = Math.max(t0, ta)
    t1 = Math.min(t1, tb)
    if (t0 > t1) return null
  }
  if (rx < 1e-9 || rz < 1e-9) return null
  const ox = la[0] / rx
  const oz = la[2] / rz
  const dx = d[0] / rx
  const dz = d[2] / rz
  const A = dx * dx + dz * dz
  const B = 2 * (ox * dx + oz * dz)
  const C = ox * ox + oz * oz - 1
  if (A < 1e-14) return C <= 0 ? [t0, t1] : null
  const disc = B * B - 4 * A * C
  if (disc < 0) return null
  const s = Math.sqrt(disc)
  t0 = Math.max(t0, (-B - s) / (2 * A))
  t1 = Math.min(t1, (-B + s) / (2 * A))
  return t0 <= t1 ? [t0, t1] : null
}

/**
 * Parametric spans of segment a→b that lie inside the solid material of the shape (empty list = misses).
 * A tube yields up to two spans: the wall on each side of an open bore.
 */
export function segmentIntervals(f: Frame, a: V3, b: V3): Span[] {
  const la = toLocal(f, a)
  const lb = toLocal(f, b)
  const d: V3 = [lb[0] - la[0], lb[1] - la[1], lb[2] - la[2]]
  if (f.shape.kind === 'box') {
    const s = clipBox(la, d, f.half)
    return s ? [s] : []
  }
  if (f.shape.kind === 'prism' && f.contours) {
    // Slab in Y, intersected with the parts of the footprint the horizontal projection of the segment passes through.
    const slab = clipBox([0, la[1], 0], [0, d[1], 0], [Number.MAX_VALUE, f.half[1], Number.MAX_VALUE])
    if (!slab) return []
    const spans: Span[] = []
    for (const [a, b] of segmentSpans(f.contours, la[0], la[2], lb[0], lb[2])) {
      const t0 = Math.max(a, slab[0])
      const t1 = Math.min(b, slab[1])
      if (t1 - t0 > 1e-9) spans.push([t0, t1])
    }
    return spans
  }
  const outer = cylSpan(la, d, f.half[0], f.half[2], -f.half[1], f.half[1])
  if (!outer) return []
  if (f.shape.kind === 'cylinder' || !f.bore) return [outer]
  const bore = cylSpan(la, d, f.bore.rx, f.bore.rz, f.bore.y0, f.bore.y1)
  if (!bore) return [outer]
  const spans: Span[] = []
  if (bore[0] > outer[0]) spans.push([outer[0], Math.min(bore[0], outer[1])])
  if (bore[1] < outer[1]) spans.push([Math.max(bore[1], outer[0]), outer[1]])
  return spans.filter((s) => s[1] - s[0] > 1e-9)
}

/** True when the segment a→b touches the shape's world-space bounding box: a cheap test before the exact one. */
export function nearSegment(f: Frame, a: V3, b: V3): boolean {
  const p = f.shape.position
  let t0 = 0
  let t1 = 1
  for (let k = 0; k < 3; k++) {
    const lo = p[k]! - f.reach[k]!
    const hi = p[k]! + f.reach[k]!
    const o = a[k]!
    const d = b[k]! - o
    if (Math.abs(d) < 1e-12) {
      if (o < lo || o > hi) return false
      continue
    }
    let ta = (lo - o) / d
    let tb = (hi - o) / d
    if (ta > tb) [ta, tb] = [tb, ta]
    if (ta > t0) t0 = ta
    if (tb < t1) t1 = tb
    if (t0 > t1) return false
  }
  return true
}
/** Length of the segment a→b that lies inside solid material of the shape (0 when it misses or threads a bore). */
export function segmentChord(f: Frame, a: V3, b: V3): number {
  if (!nearSegment(f, a, b)) return 0
  let total = 0
  for (const [t0, t1] of segmentIntervals(f, a, b)) total += t1 - t0
  return total * dist(a, b)
}

// ---- Signed distance (for walking collision) -----------------------------------------------------

function sdBox(l: V3, half: V3): number {
  const qx = Math.abs(l[0]) - half[0]
  const qy = Math.abs(l[1]) - half[1]
  const qz = Math.abs(l[2]) - half[2]
  return Math.hypot(Math.max(qx, 0), Math.max(qy, 0), Math.max(qz, 0)) + Math.min(Math.max(qx, qy, qz), 0)
}

/** Elliptical cylinders are measured in a circle-scaled space (exact when rx = rz, a close lower bound otherwise). */
function sdCyl(l: V3, rx: number, rz: number, yc: number, hh: number): number {
  const rad = (Math.hypot(l[0] / rx, l[2] / rz) - 1) * Math.min(rx, rz)
  const dy = Math.abs(l[1] - yc) - hh
  return Math.min(Math.max(rad, dy), 0) + Math.hypot(Math.max(rad, 0), Math.max(dy, 0))
}

/** Signed distance from a world point to the solid (negative inside the material). */
export function shapeSdf(f: Frame, p: V3): number {
  const l = toLocal(f, p)
  if (f.shape.kind === 'box') return sdBox(l, f.half)
  if (f.shape.kind === 'prism' && f.contours) {
    const d2 = signedDistance(f.contours, l[0], l[2])
    const dy = Math.abs(l[1]) - f.half[1]
    return Math.min(Math.max(d2, dy), 0) + Math.hypot(Math.max(d2, 0), Math.max(dy, 0))
  }
  const outer = sdCyl(l, f.half[0], f.half[2], 0, f.half[1])
  if (f.shape.kind === 'cylinder' || !f.bore) return outer
  const b = f.bore
  const bore = sdCyl(l, b.rx, b.rz, (b.y0 + b.y1) / 2, (b.y1 - b.y0) / 2)
  return Math.max(outer, -bore)
}

/**
 * Horizontal push that moves a standing body (spheres of `radius` stacked between yMin and yMax) out of a shape.
 * Works for any orientation, including a tube lying on its side. Returns null when nothing overlaps.
 */
export function pushOutXZ(f: Frame, p: V3, radius: number, yMin: number, yMax: number): V3 | null {
  const e = 0.01
  let best: V3 | null = null
  let bestMag = 0
  for (const y of [yMin, (yMin + yMax) / 2, yMax]) {
    const q: V3 = [p[0], y, p[2]]
    const d = shapeSdf(f, q) - radius
    if (d >= 0) continue
    const gx = shapeSdf(f, [q[0] + e, q[1], q[2]]) - shapeSdf(f, [q[0] - e, q[1], q[2]])
    const gz = shapeSdf(f, [q[0], q[1], q[2] + e]) - shapeSdf(f, [q[0], q[1], q[2] - e])
    const hn = Math.hypot(gx, gz)
    if (hn < 1e-6) continue
    const gy = shapeSdf(f, [q[0], q[1] + e, q[2]]) - shapeSdf(f, [q[0], q[1] - e, q[2]])
    // Mostly-vertical surfaces (a floor-like face under the body) cannot be walked out of sideways.
    if (hn / Math.hypot(gx, gy, gz) < 0.2) continue
    const amount = -d / (hn / Math.hypot(gx, gy, gz))
    const push: V3 = [(gx / hn) * amount, 0, (gz / hn) * amount]
    if (amount > bestMag) {
      bestMag = amount
      best = push
    }
  }
  return best
}
